/**
 * Displays all static site pages (About, Contact, Privacy, Terms) in a tabbed view.
 * Opens scrolled to top; the tab that was clicked (e.g. from footer) is selected.
 * Tab changes push history so browser Back walks public pages then returns to the caller.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft, Info, Mail, Shield, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  publicSystemConfigService,
  pickStaticPageHtml,
  type StaticPagesMap,
} from '../services/publicSystemConfigService';
import { useTheme } from '../hooks/useTheme';
import { useAppLocale } from '../providers/LocaleProvider';
import { darkenColor } from '../utils/colorUtils';
import { useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import { SimplePageHeader, simpleUnderHeaderClass } from './shared/SimplePageHeader';

export type StaticPageKey = 'About' | 'Contact' | 'Privacy' | 'Terms';

const PAGE_KEYS: StaticPageKey[] = ['About', 'Contact', 'Privacy', 'Terms'];

const PAGE_ICONS: Record<StaticPageKey, React.ElementType> = {
  About: Info,
  Contact: Mail,
  Privacy: Shield,
  Terms: FileText,
};

const PAGE_TITLE_KEYS: Record<StaticPageKey, string> = {
  About: 'staticAboutTitle',
  Contact: 'staticContactTitle',
  Privacy: 'staticPrivacyTitle',
  Terms: 'staticTermsTitle',
};

export interface StaticPageViewProps {
  /** Which tab to open when user navigated from a link (e.g. footer "Privacy") */
  pageKey: StaticPageKey;
  onBack: () => void;
  /** When user switches tabs — parent should push history for Back stacking. */
  onPageKeyChange?: (key: StaticPageKey) => void;
}

export const StaticPageView: React.FC<StaticPageViewProps> = ({
  pageKey,
  onBack,
  onPageKeyChange,
}) => {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const { locale, direction } = useAppLocale();
  const [pages, setPages] = useState<StaticPagesMap>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<StaticPageKey>(pageKey);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isDark } = useTheme();
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';

  useEffect(() => {
    setActiveTab(pageKey);
  }, [pageKey]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await publicSystemConfigService.getStaticPages();
        if (!cancelled) setPages(data);
      } catch {
        if (!cancelled) setPages({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
    return () => cancelAnimationFrame(id);
  }, [activeTab]);

  const html = useMemo(
    () => pickStaticPageHtml(pages[activeTab], locale),
    [pages, activeTab, locale]
  );

  const Icon = PAGE_ICONS[activeTab];
  const title = t(PAGE_TITLE_KEYS[activeTab]);
  const contentDir = locale === 'fa' ? 'rtl' : 'ltr';

  const cardGradient = isDark
    ? `linear-gradient(135deg, ${darkenColor('#e9d5ff', 0.5)}, ${darkenColor('#fce7f3', 0.5)}, ${darkenColor('#ddd6fe', 0.5)})`
    : 'linear-gradient(135deg, #f5f3ff, #fdf2f8, #ede9fe)';

  const selectTab = (key: StaticPageKey) => {
    if (key === activeTab) return;
    setActiveTab(key);
    onPageKeyChange?.(key);
  };

  const pageTabs = (
    <div className={simpleHome
      ? 'grid grid-cols-2 gap-2 sm:grid-cols-4'
      : 'grid grid-cols-2 sm:flex sm:flex-nowrap gap-1.5 sm:gap-1 p-1.5 sm:p-1 mb-6 rounded-xl bg-gray-200/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700'}>
      {PAGE_KEYS.map((key) => {
        const isActive = activeTab === key;
        const TabIcon = PAGE_ICONS[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => selectTab(key)}
            className={simpleHome
              ? `inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-3 text-sm font-semibold ${
                  isActive
                    ? 'border-transparent bg-[#3f2a56] text-[#fff8ee]'
                    : 'border-[#eadfd2] bg-[#fffdf8] text-[#2b221b] hover:border-[#d4b48a] dark:border-[#3d342c] dark:bg-[#261f1b] dark:text-[#f6efe6]'
                }`
              : `inline-flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap min-w-0 ${
                  isActive
                    ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
          >
            <TabIcon className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{t(PAGE_TITLE_KEYS[key])}</span>
          </button>
        );
      })}
    </div>
  );

  const pageBody = loading ? (
    <div className="flex flex-col items-center justify-center py-16">
      <div
        className={`w-10 h-10 border-4 border-t-transparent rounded-full animate-spin ${simpleHome ? 'border-[#3f2a56] dark:border-[#d4b48a]' : 'border-purple-500 dark:border-purple-400'}`}
        aria-hidden
      />
      <span className={`mt-4 text-sm ${simpleHome ? 'text-[#7a6a5c] dark:text-[#cbbba8]' : 'text-gray-500 dark:text-gray-400'}`}>{t('staticLoading')}</span>
    </div>
  ) : html ? (
    <div
      className={`static-page-prose leading-relaxed ${simpleHome ? 'static-page-simple text-[#2b221b] dark:text-[#f6efe6]' : 'text-gray-700 dark:text-gray-300'}`}
      dir={contentDir}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  ) : (
    <div className="text-center py-12">
      <p className={simpleHome ? 'text-[#7a6a5c] dark:text-[#cbbba8]' : 'text-gray-500 dark:text-gray-400'}>{t('staticNoContent')}</p>
    </div>
  );

  if (simpleHome) {
    return (
      <div ref={contentRef} className="min-h-screen text-[#2b221b] dark:text-[#f6efe6]" dir={direction}>
        <div className="mx-auto max-w-3xl space-y-3 px-3 py-4 sm:p-6">
          <SimplePageHeader title={title} onBack={onBack} backLabel={t('staticBack')} />
          <div className={simpleUnderHeaderClass}>{pageTabs}</div>
          <article
            className="rounded-[28px] border border-[#e4d5c3] bg-[#fffdf8] px-5 py-5 shadow-sm dark:border-[#3d342c] dark:bg-[#1c1714] sm:px-6 sm:py-6"
            aria-label={title}
          >
            {pageBody}
          </article>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={contentRef}
      className="min-h-screen bg-gray-100 dark:bg-gray-950 text-gray-900 dark:text-gray-100"
      dir={direction}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('staticBack')}
        </button>

        {pageTabs}

        <article
          className="rounded-2xl shadow-xl overflow-hidden border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900"
          aria-label={title}
        >
          <header className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4" style={{ background: cardGradient }}>
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-xl shadow-md text-white"
                style={{
                  background: isDark
                    ? 'linear-gradient(135deg, #7c3aed, #a855f7)'
                    : 'linear-gradient(135deg, #8b5cf6, #d946ef)',
                }}
              >
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                  {title}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{tCommon('appName')}</p>
              </div>
            </div>
          </header>

          <div className="px-6 sm:px-8 py-6 sm:py-8">
            {pageBody}
          </div>
        </article>
      </div>
    </div>
  );
};

export default StaticPageView;
